import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Star, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

let _reasonsCache = null;
async function fetchReasons() {
  if (_reasonsCache) return _reasonsCache;
  try {
    const { data } = await api.get("/feedback/reasons");
    _reasonsCache = data.reasons || [];
  } catch (e) {
    _reasonsCache = [];
  }
  return _reasonsCache;
}

export default function ImageRating({
  simulationId,
  imageId,
  initialRating = 0,
  initialReason = null,
  onChange,
  size = "md",
}) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(initialRating);
  const [hover, setHover] = useState(0);
  const [saving, setSaving] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState(0);
  const [chosenReason, setChosenReason] = useState(initialReason || "");
  const [note, setNote] = useState("");
  const px = size === "sm" ? 14 : size === "lg" ? 22 : 18;

  useEffect(() => {
    fetchReasons().then(setReasons);
  }, []);

  const persist = async (val, reasonId, noteText) => {
    setSaving(true);
    const prev = rating;
    setRating(val);
    try {
      const payload = { rating: val };
      if (val <= 2 && reasonId) {
        payload.feedback_reason = reasonId;
        if (noteText) payload.feedback_note = noteText;
      }
      await api.post(`/simulations/${simulationId}/images/${imageId}/rate`, payload);
      if (onChange) onChange(val);
      toast.success(
        val <= 2
          ? t("rating.toast_saved", { n: val })
          : t(val === 1 ? "rating.toast_stars_singular" : "rating.toast_stars_plural", { n: val }),
      );
    } catch (e) {
      setRating(prev);
      const detail = e.response?.data?.detail || t("rating.toast_error");
      toast.error(typeof detail === "string" ? detail : t("rating.toast_error"));
    } finally {
      setSaving(false);
    }
  };

  const submit = async (val) => {
    const prev = rating;
    if (val === prev) {
      // Click on same star = unrate
      setSaving(true);
      setRating(0);
      try {
        await api.delete(`/simulations/${simulationId}/images/${imageId}/rate`);
        if (onChange) onChange(0);
        toast.success(t("rating.toast_removed"));
      } catch (e) {
        setRating(prev);
        toast.error(t("rating.toast_error"));
      } finally {
        setSaving(false);
      }
      return;
    }
    if (val <= 2) {
      // Need to collect reason first
      setPendingValue(val);
      setChosenReason("");
      setNote("");
      setReasonOpen(true);
      return;
    }
    // 3-5 stars: persist immediately
    await persist(val, null, null);
  };

  const confirmReason = async () => {
    if (!chosenReason) {
      toast.error(t("rating.toast_choose_reason"));
      return;
    }
    setReasonOpen(false);
    await persist(pendingValue, chosenReason, note.trim() || null);
  };

  return (
    <>
      <div
        className="inline-flex items-center gap-0.5"
        data-testid={`rating-${imageId}`}
        onClick={(e) => e.stopPropagation()}
      >
        {[1, 2, 3, 4, 5].map((v) => {
          const filled = (hover || rating) >= v;
          return (
            <button
              key={v}
              type="button"
              disabled={saving}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                submit(v);
              }}
              onMouseEnter={() => setHover(v)}
              onMouseLeave={() => setHover(0)}
              className="p-0.5 transition-transform hover:scale-110 disabled:opacity-50"
              data-testid={`star-${imageId}-${v}`}
              aria-label={t("rating.tooltip_stars", { n: v })}
            >
              <Star
                size={px}
                strokeWidth={1.5}
                className={
                  filled ? "text-orange-400 fill-orange-400" : "text-zinc-600"
                }
              />
            </button>
          );
        })}
      </div>

      <Dialog open={reasonOpen} onOpenChange={(v) => !saving && setReasonOpen(v)}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-md" data-testid={`reason-dialog-${imageId}`}>
          <DialogHeader>
            <DialogTitle className="text-white">
              {t(pendingValue === 1 ? "rating.dialog_title_singular" : "rating.dialog_title_plural", { n: pendingValue })}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              {t("rating.dialog_desc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5 py-2">
            {reasons.map((r) => (
              <label
                key={r.id}
                htmlFor={`reason-${imageId}-${r.id}`}
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition select-none ${
                  chosenReason === r.id
                    ? "border-orange-500 bg-orange-500/5"
                    : "border-white/10 bg-zinc-900/40 hover:border-white/20"
                }`}
                data-testid={`reason-option-${r.id}`}
              >
                <input
                  type="radio"
                  id={`reason-${imageId}-${r.id}`}
                  name={`reason-${imageId}`}
                  checked={chosenReason === r.id}
                  onChange={() => setChosenReason(r.id)}
                  className="mt-1 accent-orange-500"
                />
                <span className="text-sm text-white leading-tight">{r.label}</span>
              </label>
            ))}
          </div>
          <Textarea
            placeholder={t("rating.note_placeholder")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            className="bg-zinc-900/60 border-white/10 text-sm resize-none focus-visible:ring-orange-500/40"
            data-testid={`reason-note-${imageId}`}
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setReasonOpen(false)}
              disabled={saving}
              className="text-zinc-400"
              data-testid={`reason-cancel-${imageId}`}
            >
              {t("rating.cancel")}
            </Button>
            <Button
              onClick={confirmReason}
              disabled={saving || !chosenReason}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid={`reason-confirm-${imageId}`}
            >
              {saving ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : null}
              {t("rating.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
